import { motion } from 'framer-motion'
import { Pencil } from 'lucide-react'
import { STOCK_SHAPES, type ShapeId } from '../../../../lib/stock-shapes'

const FIELD_LABELS: Record<string, string> = {
  material: 'Material',
  partDepth: 'Part Depth',
  stockShape: 'Stock Shape',
  stockZ: 'Stock Thickness',
  stockDims: 'Raw Stock',
  stockSize: 'Raw Stock',
  machineProfile: 'Machine',
  partFeedback: 'Feedback',
}

// Format a stock-dim answer. The question block submits a JSON blob
// of shape `{ shapeId, dimensions, x, y, z }` so we can render it
// shape-aware (e.g. "Ø45 × L100 mm" for a round bar).
function formatStockAnswer(rawText: string, units: 'mm' | 'in' = 'mm'): string {
  if (typeof rawText !== 'string' || !rawText.trim().startsWith('{')) return rawText
  try {
    const parsed = JSON.parse(rawText) as { shapeId?: ShapeId; dimensions?: Record<string, number> }
    if (parsed.shapeId && parsed.dimensions && STOCK_SHAPES[parsed.shapeId]) {
      return STOCK_SHAPES[parsed.shapeId].formatSummary(parsed.dimensions, units)
    }
  } catch { /* fall through */ }
  return rawText
}

interface Props {
  field: string
  displayText: string
  onEdit?: (field: string) => void
}

export default function UserAnswerLine({ field, displayText, onEdit }: Props) {
  const label = FIELD_LABELS[field] || field
  const isStockField = field === 'stockDims' || field === 'stockSize'
  const rendered = isStockField ? formatStockAnswer(displayText) : displayText

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.15 }}
      className="group flex items-baseline gap-2 py-1 pl-1"
    >
      <span className="font-mono text-xs text-slate-500">{label}:</span>
      <span className="font-mono text-sm text-slate-200">{rendered}</span>
      {onEdit && (
        <button
          type="button"
          onClick={() => onEdit(field)}
          className="opacity-0 group-hover:opacity-100 transition-opacity ml-1 text-slate-500 hover:text-cyan-400"
          title="Edit"
        >
          <Pencil className="w-3 h-3" />
        </button>
      )}
    </motion.div>
  )
}
